import { useState } from "react";
import { Check, Laptop, Loader2, MapPin, Pencil, Trash2, X } from "lucide-react";

import { IconRail } from "@/features/dashboard/components/IconRail";
import { Skeleton } from "@/components/ui/skeleton";
import { useDevices, useRenameDevice, useRevokeDevice } from "./api/hooks";
import type { Device } from "./api/types";

/** "Dine enheter" — the trusted-device directory. Same icon rail + teal→navy gradient as the
 *  chat dashboard. Available to both regular users and admins. Routed at /enheter. */
export function DevicesPage() {
  const { data: devices, isLoading, isError } = useDevices();

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[radial-gradient(120%_120%_at_50%_0%,#134e4a_0%,#0a2a2e_42%,#020617_100%)] text-white">
      <IconRail />

      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-2xl">
          <h1 className="text-2xl font-semibold">Dine enheter</h1>
          <p className="mt-2 text-sm text-white/60">
            Enheter du har bekreftet. Fjerner du en, må neste innlogging derfra bekreftes på nytt.
          </p>

          <section className="mt-8 space-y-3">
            {isLoading && (
              <>
                <Skeleton className="h-24 rounded-2xl" />
                <Skeleton className="h-24 rounded-2xl" />
              </>
            )}

            {isError && (
              <div className="rounded-2xl border border-red-400/20 bg-red-400/10 p-6 text-sm text-red-200">
                Kunne ikke laste enhetene dine. Prøv igjen senere.
              </div>
            )}

            {!isLoading && !isError && devices?.length === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
                Ingen betrodde enheter ennå.
              </div>
            )}

            {!isLoading &&
              !isError &&
              devices?.map((d) => <DeviceRow key={d.id} device={d} />)}
          </section>
        </div>
      </main>
    </div>
  );
}

/** Coarse "city, country" from the stored geo JSON — informational only. */
function formatGeo(json: string | null): string | null {
  if (!json) return null;
  try {
    const g = JSON.parse(json) as { city?: string | null; country?: string | null };
    const parts = [g.city, g.country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  } catch {
    return null;
  }
}

/** "13.07.2026, 20:00" from the backend's "YYYY-MM-DD HH:MM:SS" (UTC). */
function formatSeen(s: string | null): string | null {
  if (!s) return null;
  const d = new Date(s.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DeviceRow({ device }: { device: Device }) {
  const rename = useRenameDevice();
  const revoke = useRevokeDevice();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(device.name ?? "");

  const geo = formatGeo(device.lastGeo);
  const seen = formatSeen(device.lastSeenAt);

  const save = async () => {
    const name = value.trim();
    if (!name) return;
    try {
      await rename.mutateAsync({ id: device.id, name });
      setEditing(false);
    } catch {
      /* keep editor open; the mutation error is transient */
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <div className="flex items-start gap-4">
        <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl bg-teal-400/15 text-teal-300">
          <Laptop className="size-5" />
        </span>

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") setEditing(false);
                }}
                maxLength={80}
                placeholder="Enhetsnavn"
                className="h-8 w-56 max-w-full rounded-md border border-white/15 bg-white/5 px-2 text-sm text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
              />
              <button
                type="button"
                onClick={save}
                disabled={rename.isPending || !value.trim()}
                aria-label="Lagre navn"
                className="flex size-8 items-center justify-center rounded-md bg-teal-400 text-[#04231d] disabled:opacity-40"
              >
                {rename.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue(device.name ?? "");
                  setEditing(false);
                }}
                aria-label="Avbryt"
                className="flex size-8 items-center justify-center rounded-md border border-white/15 text-white/60 hover:bg-white/5"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-white">{device.name ?? "Ukjent enhet"}</span>
              {device.current && (
                <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-inset ring-emerald-500/30">
                  Denne enheten
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setValue(device.name ?? "");
                  setEditing(true);
                }}
                aria-label="Gi nytt navn"
                title="Gi nytt navn"
                className="text-white/40 transition-colors hover:text-white/70"
              >
                <Pencil className="size-3.5" />
              </button>
            </div>
          )}

          {device.userAgent && (
            <p className="mt-1 truncate text-xs text-white/40" title={device.userAgent}>
              {device.userAgent}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/50">
            {seen && <span>Sist aktiv {seen}</span>}
            {(geo || device.lastIp) && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-3.5 text-teal-300/70" />
                {geo ?? device.lastIp}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => revoke.mutate(device.id)}
          disabled={revoke.isPending}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-white/15 px-3 text-xs font-medium text-white/70 transition-colors hover:border-red-400/40 hover:bg-red-400/10 hover:text-red-200 disabled:opacity-40"
        >
          {revoke.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Trash2 className="size-3.5" />
          )}
          Fjern
        </button>
      </div>
    </div>
  );
}
